import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useWaterStore, useWaterHydrated } from '@/stores/water-store';
import { useFamilyStore } from '@/stores/family-store';
import { useAuthStore, useAuthHydrated } from '@/stores/auth-store';
import PremiumRequiredModal from '@/components/PremiumRequiredModal';
import ModalBackdrop from '@/components/ModalBackdrop';
import { forceUnlockAll } from '@/utils/scrollLock';
import { 
  Droplets, 
  Plus,
  Minus, 
  Users, 
  Calendar, 
  AlertTriangle,
  CheckCircle2,
  Info,
  Trash2,
  RefreshCw,
  Dog,
  Copy,
  ChevronLeft,
  Package,
  Pencil,
} from 'lucide-react';

export default function WaterPage() {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuthStore();
  const containersRef = useRef<HTMLDivElement>(null);
  const family = useFamilyStore((s) => s.family);
  const authHydrated = useAuthHydrated();
  const waterHydrated = useWaterHydrated();
  
  // Water store
  const {
    householdSize,
    pets,
    targetDays,
    containers,
    stats,
    setHouseholdSize,
    setPets,
    setTargetDays,
    addContainer,
    updateContainer,
    deleteContainer,
    refreshContainer,
    fetchFromServer,
  } = useWaterStore();
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [editingContainer, setEditingContainer] = useState<typeof containers[0] | null>(null);
  const [confirmRefreshContainerId, setConfirmRefreshContainerId] = useState<string | null>(null);
  const [confirmDeleteContainerId, setConfirmDeleteContainerId] = useState<string | null>(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  // New container form
  const [newContainer, setNewContainer] = useState({
    name: '',
    liters: 10,
    location: '',
    filledDate: new Date().toISOString().split('T')[0],
  });

  // Hämta data från server vid mount om inloggad (vänta på BÅDA hydrations)
  useEffect(() => {
    if (authHydrated && waterHydrated && isAuthenticated) {
      const familyId = useFamilyStore.getState().family?.id;
      // If viewing as a family member, this will ensure we fetch the owner's water data
      // instead of the member's personal data.
      fetchFromServer(familyId);
    }
  }, [authHydrated, waterHydrated, isAuthenticated, fetchFromServer]);
  // Gemensam modal-stateindikator för scroll-lock-säkerhet
  const isAnyModalOpen = showAddForm || !!editingContainer || !!confirmRefreshContainerId || !!confirmDeleteContainerId;

  // Safety-net: om inga modaler är öppna men scroll fortfarande är låst, forcera upplåsning
  useEffect(() => {
    if (isAnyModalOpen) return;
    const timer = setTimeout(() => {
      try {
        const cur = parseInt(document.body.dataset.scrollLockCount || '0', 10) || 0;
        if (cur > 0) {
          try { forceUnlockAll(); } catch (_e) { /* noop */ }
        }
      } catch (_e) { /* noop */ }
    }, 250);

    return () => clearTimeout(timer);
  }, [isAnyModalOpen]);

  // Använd stats från store
  const { totalLiters, daysSupply, targetLiters, progress, containersNeedingRefresh } = stats;

  // Kolla om något vatten behöver bytas (äldre än 6 månader)
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  
  const containersNeedingRefreshDetails = containers.filter(c => 
    containersNeedingRefresh.includes(c.id)
  );

  // Konstanter för visning
  const litersPerPersonPerDay = 5;
  const litersPerPetPerDay = 1;

  const handleAddContainer = () => {
    if (!newContainer.name || newContainer.liters <= 0) return;
    
    addContainer({
      name: newContainer.name,
      liters: newContainer.liters,
      filledDate: newContainer.filledDate,
      location: newContainer.location || 'Okänd',
    });
    
    setNewContainer({ name: '', liters: 10, location: '', filledDate: new Date().toISOString().split('T')[0] });
    setShowAddForm(false);
    setIsCopying(false);
  };

  const copyContainer = (container: typeof containers[0]) => {
    // Check limits: Free tier = 10 containers, Premium = 100 containers
    const hasPremiumAccess = user?.subscriptionTier === 'premium' || !!family;
    const FREE_TIER_LIMIT = 10;
    const PREMIUM_LIMIT = 100;
    
    if (!hasPremiumAccess && containers.length >= FREE_TIER_LIMIT) {
      setShowPremiumModal(true);
      return;
    }
    
    if (hasPremiumAccess && containers.length >= PREMIUM_LIMIT) {
      // Premium users can't add more than 100 containers
      return;
    }

    setNewContainer({
      name: container.name,
      liters: container.liters,
      location: container.location,
      filledDate: new Date().toISOString().split('T')[0], // Nytt datum
    });
    setIsCopying(true);
    setShowAddForm(true);
  };

  const handleDeleteContainer = (id: string) => {
    deleteContainer(id);
  };

  const handleRefreshContainer = (id: string) => {
    refreshContainer(id);
  };

  const getStatusColor = () => {
    if (daysSupply >= targetDays) return 'text-green-400';
    if (daysSupply >= targetDays * 0.5) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getProgressColor = () => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center"><Droplets className="h-6 w-6 text-primary mr-3 shrink-0" />{t('water.title')}</h1>
        <p className="text-muted-foreground mt-1">Håll koll på vattenlager och roteringsschema.</p>
      </div>

      {/* Förrådslänk */}
      <Link
        to="/app/inventory"
        className="flex items-center justify-between w-full p-4 rounded-xl bg-orange-400/10 border border-orange-400/30 hover:bg-orange-400/20 transition-all"
      >
        <div className="flex items-center gap-3">
          <ChevronLeft className="h-6 w-6 text-orange-400" />
          <p className="font-semibold text-orange-400">Beredskapsförråd</p>
        </div>
        <Package className="h-5 w-5 text-orange-400" />
      </Link>

      {/* Varning om vatten som behöver bytas */}
      {containersNeedingRefreshDetails.length > 0 && (
        <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 p-4 cursor-pointer" onClick={() => {
            if (!containersRef.current) return;
            const isMobile = window.innerWidth < 768;
            const yOffset = isMobile ? -80 : -16;
            const y = containersRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }}>
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-400">Vatten behöver bytas</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {containersNeedingRefreshDetails.length} behållare har vatten som är äldre än 6 månader och bör bytas ut.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {containersNeedingRefreshDetails.map(c => (
                  <span key={c.id} className="text-xs bg-yellow-500/20 px-2 py-1 rounded">
                    {c.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Huvudkort - Vattenöversikt */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {/* Vänster: Ikon och status */}
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-blue-500/20 p-4">
              <Droplets className="h-10 w-10 text-blue-400" />
            </div>
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">{totalLiters}</span>
                <span className="text-muted-foreground">liter</span>
              </div>
              <p className={`text-lg font-semibold ${getStatusColor()}`}>
                {daysSupply.toFixed(1)} dagars förråd
              </p>
            </div>
          </div>

          {/* Höger: Progress */}
          <div className="flex-1">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">
                Mål: {targetLiters} liter ({targetDays} dagar)
              </span>
              <span className="font-semibold">{Math.round(progress)}%</span>
            </div>
            <div className="h-4 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getProgressColor()}`}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            {progress >= 100 && (
              <div className="flex items-center gap-1 mt-2 text-green-400 text-sm">
                <CheckCircle2 className="h-4 w-4" />
                Målet är uppnått!
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="rounded-xl bg-blue-500/10 border border-blue-500/20 p-6">
        <div className="flex items-start gap-4">
          <Info className="h-6 w-6 text-blue-400 shrink-0" />
          <div>
            <h3 className="font-semibold">Tips:</h3>
            <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
              <li>• <strong>5 liter per person och dygn</strong> – räkna med dricksvatten, matlagning och hygien</li>
              <li>• <strong>Byt ut vattnet var 6:e månad</strong> – midsommar och jul är lätta att komma ihåg</li>
              <li>• <strong>Dunkar med vridkran</strong> – mycket smidigare att använda än att lyfta och hälla</li>
              <li>• <strong>Förvara mörkt och svalt</strong> – undvik direkt solljus för längre hållbarhet</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Inställningar */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold">Hushåll</h2>
          <p className="text-sm text-muted-foreground mt-1">Speciella hushållsinställning för vattenförbrukning.</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-3">
          {/* Antal personer */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              <Users className="h-4 w-4" />
              Antal personer (vuxna och barn)
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setHouseholdSize(Math.max(1, householdSize - 1))}
                className="h-10 w-10 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                -
              </button>
              <span className="w-12 text-center text-xl font-semibold">{householdSize}</span>
              <button
                onClick={() => setHouseholdSize(Math.min(100, householdSize + 1))}
                className="h-10 w-10 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                +
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {householdSize * litersPerPersonPerDay} liter per dag
            </p>
          </div>

          {/* Husdjur */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              <Dog className="h-4 w-4" />
              Husdjur
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPets(Math.max(1, pets - 1))}
                className="h-10 w-10 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                -
              </button>
              <span className="w-12 text-center text-xl font-semibold">{pets}</span>
              <button
                onClick={() => setPets(Math.min(100, pets + 1))}
                className="h-10 w-10 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                +
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {pets > 0 ? `+${pets * litersPerPetPerDay} liter per dag` : 'Inga husdjur'}
            </p>
          </div>

          {/* Målperiod */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              <Calendar className="h-4 w-4" />
              Beredskapsmål (för vatten)
            </label>
            <select
              value={targetDays}
              onChange={(e) => setTargetDays(Number(e.target.value))}
              className="w-full h-10 px-3 rounded-lg border border-border bg-background"
            >
              <option value={3}>3 dagar</option>
              <option value={7}>7 dagar</option>
              <option value={14}>14 dagar</option>
              <option value={30}>30 dagar</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Behov: {targetLiters} liter totalt
            </p>
          </div>
        </div>
      </div>

      {/* Vattenbehållare */}
      <div ref={containersRef}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Dina vattenbehållare</h2>
          <button
            onClick={() => { 
              // Check limits: Free tier = 10 containers, Premium = 100 containers
              const hasPremiumAccess = user?.subscriptionTier === 'premium' || !!family;
              const FREE_TIER_LIMIT = 10;
              const PREMIUM_LIMIT = 100;
              
              if (!hasPremiumAccess && containers.length >= FREE_TIER_LIMIT) {
                setShowPremiumModal(true);
                return;
              }
              
              if (hasPremiumAccess && containers.length >= PREMIUM_LIMIT) {
                // Premium users can't add more than 100 containers
                return;
              }
              
              setIsCopying(false); 
              setShowAddForm(true); 
            }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg btn-primary-custom transition-colors"
            >
              <Plus className="h-4 w-4" />
              Lägg till
            </button>
          </div>

          {containers.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-border p-8 text-center">
              <Droplets className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">Inga vattenbehållare tillagda ännu</p>
              <button
                onClick={() => { setIsCopying(false); setShowAddForm(true); }}
                className="mt-4 px-4 py-2 rounded-lg btn-primary-custom"
              >
                Lägg till din första behållare
              </button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {containers.map((container) => {
              const filledDate = new Date(container.filledDate);
              const needsRefresh = filledDate < sixMonthsAgo;
              
              return (
                <div
                  key={container.id}
                  className={`rounded-lg border bg-card p-4 ${
                    needsRefresh ? 'border-yellow-500/50' : 'border-border'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 min-w-0">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`rounded-lg p-2 flex-shrink-0 ${needsRefresh ? 'bg-yellow-500/20' : 'bg-blue-500/20'}`}>
                        <Droplets className={`h-5 w-5 ${needsRefresh ? 'text-yellow-400' : 'text-blue-400'}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium soft-wrap">{container.name}</h3>
                        <p className="text-sm text-muted-foreground soft-wrap">{container.location}</p>
                      </div>
                    </div>
                    <span className="text-xl font-bold flex-shrink-0">{container.liters}L</span>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      Fylld: {filledDate.toLocaleDateString('sv-SE')}
                      {needsRefresh && (
                        <span className="text-yellow-400 ml-1">(byt ut!)</span>
                      )}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingContainer(container)}
                        className="p-1.5 rounded hover:bg-muted transition-colors"
                        title="Redigera"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => copyContainer(container)}
                        className="p-1.5 rounded hover:bg-muted transition-colors"
                        title="Kopiera"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setConfirmRefreshContainerId(container.id)}
                        className="p-1.5 rounded hover:bg-muted transition-colors"
                        title="Markera som bytt"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setConfirmDeleteContainerId(container.id)}
                        className="p-1.5 rounded hover:bg-red-500/20 hover:text-red-400 transition-colors"
                        title="Ta bort"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          )}
      </div>

      {/* Modal för att lägga till behållare */}
      {showAddForm && (
            <ModalBackdrop onClick={() => { setShowAddForm(false); setIsCopying(false); }}>
              <div className="w-full max-w-md rounded-xl bg-card border border-border p-6" onClick={(e) => { e.stopPropagation(); }}>
            <h2 className="text-xl font-semibold mb-4">{isCopying ? 'Kopiera vattenbehållare' : 'Lägg till vattenbehållare'}</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Namn</label>
                <input
                  type="text"
                  value={newContainer.name}
                  onChange={(e) => setNewContainer({ ...newContainer, name: e.target.value })}
                  placeholder="T.ex. Blå dunk"
                  maxLength={50}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background break-words"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Volym (liter)</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setNewContainer({ ...newContainer, liters: Math.max(0, newContainer.liters - 1) })}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    value={newContainer.liters}
                    onChange={(e) => setNewContainer({ ...newContainer, liters: Math.min(1000, Math.max(0, Number(e.target.value))) })}
                    min={0}
                    max={1000}
                    className="w-20 px-3 py-2 rounded-lg border border-border bg-background text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    onClick={() => setNewContainer({ ...newContainer, liters: Math.min(1000, newContainer.liters + 1) })}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Förvaringsplats</label>
                <input
                  type="text"
                  value={newContainer.location}
                  onChange={(e) => setNewContainer({ ...newContainer, location: e.target.value })}
                  placeholder="T.ex. Förråd, Garage"
                  maxLength={50}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background break-words"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Fyllt datum</label>
                <input
                  type="date"
                  value={newContainer.filledDate}
                  onChange={(e) => setNewContainer({ ...newContainer, filledDate: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>

              <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowAddForm(false); setIsCopying(false); }}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleAddContainer}
                disabled={!newContainer.name || newContainer.liters <= 0}
                className="flex-1 px-4 py-2 rounded-lg btn-primary-custom disabled:opacity-50"
              >
                Lägg till
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Modal för att redigera behållare */}
      {editingContainer && (
        <ModalBackdrop onClick={() => setEditingContainer(null)}>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-6" onClick={(e) => { e.stopPropagation(); }}>
            <h2 className="text-xl font-semibold mb-4">Redigera vattenbehållare</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Namn</label>
                <input
                  type="text"
                  value={editingContainer.name}
                  onChange={(e) => setEditingContainer({ ...editingContainer, name: e.target.value })}
                  placeholder="T.ex. Blå dunk"
                  maxLength={50}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background break-words"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Volym (liter)</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingContainer({ ...editingContainer, liters: Math.max(0, editingContainer.liters - 1) })}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    value={editingContainer.liters}
                    onChange={(e) => setEditingContainer({ ...editingContainer, liters: Math.min(1000, Math.max(0, Number(e.target.value))) })}
                    min={0}
                    max={1000}
                    className="w-20 px-3 py-2 rounded-lg border border-border bg-background text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    onClick={() => setEditingContainer({ ...editingContainer, liters: Math.min(1000, editingContainer.liters + 1) })}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Förvaringsplats</label>
                <input
                  type="text"
                  value={editingContainer.location}
                  onChange={(e) => setEditingContainer({ ...editingContainer, location: e.target.value })}
                  placeholder="T.ex. Förråd, Garage"
                  maxLength={50}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background break-words"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Fyllt datum</label>
                <input
                  type="date"
                  value={editingContainer.filledDate}
                  onChange={(e) => setEditingContainer({ ...editingContainer, filledDate: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditingContainer(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  if (editingContainer.name && editingContainer.liters > 0) {
                    updateContainer(editingContainer.id, {
                      name: editingContainer.name,
                      liters: editingContainer.liters,
                      location: editingContainer.location,
                      filledDate: editingContainer.filledDate,
                    });
                    setEditingContainer(null);
                  }
                }}
                disabled={!editingContainer.name || editingContainer.liters <= 0}
                className="flex-1 px-4 py-2 rounded-lg btn-primary-custom disabled:opacity-50"
              >
                Spara
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Bekräftelsedialog för vattenbytt */}
      {confirmRefreshContainerId && (
        <ModalBackdrop onClick={() => setConfirmRefreshContainerId(null)}>
          <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6" onClick={(e) => { e.stopPropagation(); }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <RefreshCw className="h-6 w-6 text-blue-400" />
              </div>
              <h2 className="text-xl font-semibold">Bekräfta vattenbyte</h2>
            </div>
            <p className="text-muted-foreground mb-6">
              Vill du markera denna behållare som nyligen bytt? Datumet för när vattnet fylldes på uppdateras till idag.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmRefreshContainerId(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  handleRefreshContainer(confirmRefreshContainerId);
                  setConfirmRefreshContainerId(null);
                }}
                className="flex-1 px-4 py-2 rounded-lg btn-primary-custom transition-colors"
              >
                Bekräfta
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Bekräftelsedialog för borttagning */}
      {confirmDeleteContainerId && (() => {
        const container = containers.find(c => c.id === confirmDeleteContainerId);
        return (
          <ModalBackdrop onClick={() => setConfirmDeleteContainerId(null)}>
            <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6" onClick={(e) => { e.stopPropagation(); }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <Trash2 className="h-6 w-6 text-red-400" />
                </div>
                <h2 className="text-xl font-semibold">Bekräfta borttagning</h2>
              </div>
              <p className="text-muted-foreground mb-2">
                Är du säker på att du vill ta bort <span className="font-medium text-foreground">{container?.name}</span>?
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Denna behållare innehåller {container?.liters} liter vatten.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDeleteContainerId(null)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={() => {
                    handleDeleteContainer(confirmDeleteContainerId);
                    setConfirmDeleteContainerId(null);
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  Ta bort
                </button>
              </div>
            </div>
          </ModalBackdrop>
        );
      })()}

      {/* Premium Required Modal */}
      <PremiumRequiredModal
        open={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature="Fler än 10 vattenbehållare"
      />
    </div>
  );
}
